import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { type ClassValue } from "clsx"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex justify-center items-center flat:!shadow-none flat:active:py-0 flat:py-0 active:pt-[0.0625rem] pb-[0.0625rem] active:pb-0 rounded outline-none font-body font-semibold disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary:
          "bg-[#0133BC] text-white shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] hover:bg-[#014BDB] active:shadow-sm active:from-primary-800 active:to-primary-700 active:shadow-primary-800 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:text-gray-400 disabled:bg-[#CDD5DF] rounded-xl",
        secondary:
          "bg-[#FCFCFD] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-[#0133BC] border border-[#E3E8EF] rounded-xl hover:bg-[#E3E8EF] hover:shadow-gray-200 active:shadow-sm active:from-gray-200 active:to-gray-100 disabled:bg-[#E3E8EF] disabled:text-[#CDD5DF]",
        tertiary:
          "bg-transparent border border-transparent text-gray-700 hover:bg-gradient-to-b hover:from-white hover:to-gray-50 hover:shadow-inner hover:shadow-gray-100 hover:border-gray-200 hover:text-primary-600 active:shadow-sm active:bg-gradient-to-b active:from-gray-100 active:to-gray-50 active:shadow-gray-100 active:border-gray-200 focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:text-gray-300 disabled:shadow-none disabled:border-none disabled:bg-none",
        destructive:
          "bg-gradient-to-b to-error-50 from-white shadow-inner shadow-error-100 text-error-500 border border-error-200 hover:from-error-50 hover:to-error-100 hover:shadow-error-200 hover:border-error-500 hover:text-error-600 active:shadow-sm active:from-error-200 active:to-error-100 active:text-error-700 active:border-error-600 active:shadow-error-200 focus-visible:ring-2 focus-visible:ring-error-500 focus-visible:ring-offset-2 disabled:text-gray-400 disabled:bg-gray-100 disabled:bg-none disabled:shadow-gray-200 disabled:border-gray-300",
      },
      size: {
        sm: "px-3 h-9 text-sm",
        md: "px-3 h-10 text-md",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  leftIconClassName?: ClassValue
  rightIconClassName?: ClassValue
  textClassName?: ClassValue
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      type = "button",
      leftIcon,
      rightIcon,
      leftIconClassName,
      rightIconClassName,
      textClassName,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        {...props}
      >
        {leftIcon && (
          <span
            className={cn(
              "flex size-5 min-w-5 items-center justify-center",
              {
                "size-4": size === "sm",
              },
              leftIconClassName
            )}
          >
            {leftIcon}
          </span>
        )}
        <span className={cn("flex min-w-5 justify-center px-2", textClassName)}>
          {children}
        </span>
        {rightIcon && (
          <span
            className={cn(
              "flex size-5 min-w-5 items-center justify-center",
              {
                "size-4": size === "sm",
              },
              rightIconClassName
            )}
          >
            {rightIcon}
          </span>
        )}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
